import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightOnRectangleIcon, Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import logo from "../assets/img/hec-logo.png";
import { ROLES, homePath, roleLabel } from "../auth/roles";
import { getDecodedToken, hasAnyRole, logout } from "../utils/auth";
import { Dropdown, DropdownItem, Loading } from "../components/ui";
import NotificationBell from "./NotificationBell";

// Name and email from the access token; Keycloak and password-login tokens use different claims
const currentUser = () => {
  const token = getDecodedToken() ?? {};
  const fullName =
    token.name ||
    [token.given_name, token.family_name].filter(Boolean).join(" ") ||
    [token.firstName, token.lastName].filter(Boolean).join(" ");
  const email = token.email || token.preferred_username || token.sub || "";
  return { name: fullName || email, email };
};

const initials = (name) =>
  name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

export default function Navbar({ onMenuClick }) {
  const user = currentUser();
  const role = roleLabel();
  // Signing out leaves for Keycloak and back, so cover the page until the browser goes
  const [signingOut, setSigningOut] = useState(false);

  const signOut = () => {
    setSigningOut(true);
    logout();
  };

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center border-b border-slate-100 bg-white">
      {/* Logo cell is as wide as the sidebar, so the content area lines up below */}
      <div className="flex h-full shrink-0 items-center gap-3 border-slate-100 px-4 md:w-64 md:border-r md:px-6 lg:w-72">
        <button type="button" onClick={onMenuClick} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden" aria-label="Open menu">
          <Bars3Icon className="h-5 w-5" />
        </button>
        <Link to={homePath()} className="flex items-center gap-3">
          <img src={logo} alt="Higher Education Council" className="h-11 w-auto" />
        </Link>
      </div>

      <div className="flex flex-1 items-center justify-end gap-2 px-4 md:px-6">
        {hasAnyRole(ROLES.APPLICANT) && <NotificationBell />}
        {hasAnyRole(ROLES.SCHOLARSHIP_OWNER) && <NotificationBell scope="staff" />}
        <Dropdown
          trigger={
            <span className="flex items-center gap-2 rounded-md p-2 transition-all duration-100 hover:bg-slate-50">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-brand/30 bg-brand/5 text-sm font-bold text-brand">
                {initials(user.name) || "?"}
              </span>
              <span className="hidden flex-col text-left sm:flex">
                <span className="text-sm font-bold text-slate-900">{user.name}</span>
                {role && <span className="text-sm capitalize text-slate-500">{role}</span>}
              </span>
              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
            </span>
          }
        >
          <div className="flex flex-col gap-1 border-b border-gray-100 px-3 py-2">
            <span className="text-sm font-medium text-gray-900">{user.email}</span>
            {role && <span className="text-xs capitalize text-gray-500">{role}</span>}
          </div>
          <DropdownItem danger icon={<ArrowRightOnRectangleIcon className="h-4 w-4" />} onClick={signOut}>
            Sign Out
          </DropdownItem>
        </Dropdown>
      </div>

      {signingOut && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm" role="status" aria-live="polite">
          <Loading label="Signing you out…" />
        </div>
      )}
    </header>
  );
}
